<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisField from 'dashboard/components-next/LaisField.vue';
import SalesCommissionInstallmentCard from './SalesCommissionInstallmentCard.vue';

const props = defineProps({
  errors: { type: Object, default: () => ({}) },
  form: { type: Object, required: true },
  payerOptions: { type: Array, default: () => [] },
});

const emit = defineEmits([
  'changeCount',
  'update',
  'updateNotes',
  'updatePayer',
  'updatePayerCount',
]);
const { t } = useI18n();
const installments = computed(() => props.form.installments || []);
const disabledPaymentTriggers = index =>
  installments.value
    .filter((_, installmentIndex) => installmentIndex !== index)
    .map(installment => installment.payment_trigger)
    .filter(Boolean);
</script>

<template>
  <LaisCard :title="t('SALES.COMMISSION.PAYMENT.TITLE')">
    <div class="mb-11 grid gap-11">
      <div class="flex flex-wrap gap-4">
        <label
          class="flex items-center gap-2 text-sm font-semibold text-lais-text"
        >
          <input
            type="radio"
            name="commission-modality"
            value="integral"
            :checked="form.modality === 'integral'"
            @change="emit('changeCount', 1)"
          />
          {{ t('SALES.COMMISSION.MODALITIES.INTEGRAL') }}
        </label>
        <label
          class="flex items-center gap-2 text-sm font-semibold text-lais-text"
        >
          <input
            type="radio"
            name="commission-modality"
            value="partes"
            :checked="form.modality === 'partes'"
            @change="emit('changeCount', Math.max(2, installments.length || 2))"
          />
          {{ t('SALES.COMMISSION.MODALITIES.INSTALLMENTS') }}
        </label>
      </div>
      <div
        v-if="form.modality === 'partes'"
        class="max-w-[320px]"
        data-test="commission-installment-count"
      >
        <LaisField
          :label="t('SALES.COMMISSION.FIELDS.INSTALLMENT_COUNT')"
          required
        >
          <select
            :value="installments.length"
            class="min-h-10 w-full rounded-lais border border-lais-border bg-white px-3 text-sm"
            @change="emit('changeCount', $event.target.value)"
          >
            <option v-for="count in 20" :key="count" :value="count">
              {{ count }}
            </option>
          </select>
        </LaisField>
      </div>
    </div>
    <p v-if="errors.form" class="mb-4 text-sm text-lais-danger">
      {{ errors.form }}
    </p>
    <div
      v-if="!installments.length"
      class="rounded-lais border border-dashed border-lais-border p-6 text-center text-sm text-lais-text-muted"
    >
      {{ t('SALES.COMMISSION.PAYMENT.EMPTY') }}
    </div>
    <div v-else class="grid gap-4">
      <SalesCommissionInstallmentCard
        v-for="(installment, index) in installments"
        :key="index"
        :index="index"
        :installment="installment"
        :installment-count="installments.length"
        :net-amount="form.net_amount"
        :errors="errors.rows?.[index] || {}"
        :disabled-payment-triggers="disabledPaymentTriggers(index)"
        :payer-options="payerOptions"
        @update="(field, value) => emit('update', index, field, value)"
        @update-payer="
          (payerIndex, field, value) =>
            emit('updatePayer', index, payerIndex, field, value)
        "
        @update-payer-count="count => emit('updatePayerCount', index, count)"
      />
    </div>
    <div class="mt-7">
      <p class="mb-3 text-sm font-semibold text-lais-text">
        {{ t('SALES.COMMISSION.PAYMENT.NOTES_TITLE') }}
      </p>
      <LaisField :label="t('SALES.COMMISSION.PAYMENT.NOTES_LABEL')">
        <textarea
          :value="form.notes"
          rows="3"
          class="w-full rounded-lais border border-lais-border bg-white px-3 py-2 text-sm"
          :placeholder="t('SALES.COMMISSION.PLACEHOLDERS.NOTES')"
          @input="emit('updateNotes', $event.target.value)"
        />
      </LaisField>
    </div>
  </LaisCard>
</template>
